import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { ArrowLeft, Calendar, Sparkles, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Tiny markdown renderer for the subset the LLM outputs:
 *  ## heading, ### heading, **bold**, *italic*, - bullet, blank-line paragraph breaks. */
function Markdown({ text }) {
    if (!text) return null;
    const lines = text.split(/\r?\n/);
    const out = [];
    let bulletBuffer = [];

    const flushBullets = () => {
        if (bulletBuffer.length) {
            out.push(
                <ul key={`ul-${out.length}`} className="list-disc pl-6 space-y-1 my-4 text-forest/90">
                    {bulletBuffer.map((b, i) => <li key={i}>{renderInline(b)}</li>)}
                </ul>
            );
            bulletBuffer = [];
        }
    };

    const renderInline = (s) => {
        // bold / italic
        const parts = [];
        let rem = s;
        let key = 0;
        const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/;
        while (rem) {
            const m = rem.match(re);
            if (!m) { parts.push(rem); break; }
            if (m.index > 0) parts.push(rem.slice(0, m.index));
            if (m[2] !== undefined) parts.push(<strong key={key++}>{m[2]}</strong>);
            else parts.push(<em key={key++}>{m[3]}</em>);
            rem = rem.slice(m.index + m[0].length);
        }
        return parts;
    };

    lines.forEach((raw, i) => {
        const line = raw.trimEnd();
        if (line.startsWith("### ")) {
            flushBullets();
            out.push(<h3 key={i} className="font-serif text-xl text-forest mt-6 mb-2">{renderInline(line.slice(4))}</h3>);
        } else if (line.startsWith("## ")) {
            flushBullets();
            out.push(<h2 key={i} className="font-serif text-2xl text-forest mt-8 mb-3">{renderInline(line.slice(3))}</h2>);
        } else if (line.startsWith("# ")) {
            flushBullets();
            out.push(<h1 key={i} className="font-serif text-3xl text-forest mt-8 mb-3">{renderInline(line.slice(2))}</h1>);
        } else if (/^[-*]\s+/.test(line)) {
            bulletBuffer.push(line.replace(/^[-*]\s+/, ""));
        } else if (line.trim() === "") {
            flushBullets();
        } else {
            flushBullets();
            out.push(<p key={i} className="text-forest/90 leading-relaxed my-4">{renderInline(line)}</p>);
        }
    });
    flushBullets();
    return <div>{out}</div>;
}

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        let alive = true;
        api.get(`/blog/posts/${slug}`)
            .then(({ data }) => { if (alive) { setPost(data); setStatus("ok"); } })
            .catch(() => { if (alive) setStatus("notfound"); });
        return () => { alive = false; };
    }, [slug]);

    if (status === "loading") {
        return (
            <div className="bg-cream min-h-screen flex items-center justify-center">
                <Loader2 size={20} className="animate-spin text-gold" />
            </div>
        );
    }

    if (status === "notfound" || !post) {
        return (
            <div className="bg-cream min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="blog-post-notfound">
                <Sparkles size={28} className="text-gold" />
                <h1 className="font-serif text-3xl text-forest mt-4">Entry not found</h1>
                <p className="text-sm text-brown mt-2">This post may have been unpublished or the link is broken.</p>
                <Link to="/blog" className="mt-6 btn-pill btn-primary"><ArrowLeft size={14} /> Back to the Journal</Link>
            </div>
        );
    }

    const seo = post.seo || {};
    const ogImage = post.og_image_url ? `${BACKEND}${post.og_image_url}` : undefined;

    return (
        <div className="bg-cream min-h-screen" data-testid="blog-post-page">
            <Seo
                title={seo.title || post.title}
                description={seo.description || post.excerpt}
                image={ogImage}
            />
            <article className="max-w-3xl mx-auto px-5 md:px-10 pt-16 pb-24">
                <Link to="/blog" className="inline-flex items-center gap-1.5 text-[0.65rem] tracking-[0.3em] uppercase text-brown hover:text-gold">
                    <ArrowLeft size={12} /> Back to the Journal
                </Link>

                <div className="mt-8 flex items-center gap-2 text-[0.6rem] tracking-[0.3em] uppercase text-brown/60">
                    <Calendar size={11} />
                    {new Date(post.approved_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </div>

                <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02] text-forest mt-4" data-testid="blog-post-title">
                    {post.title}
                </h1>
                <p className="mt-6 text-xl text-brown italic leading-relaxed">{post.excerpt}</p>

                {ogImage && (
                    <div className="mt-10 aspect-[16/9] overflow-hidden rounded-sm border border-forest/15">
                        <img src={ogImage} alt={post.title} className="w-full h-full object-cover" />
                    </div>
                )}

                <div className="mt-10" data-testid="blog-post-body">
                    <Markdown text={post.body_markdown} />
                </div>

                {post.suggested_tags?.length > 0 && (
                    <div className="mt-10 pt-6 border-t border-forest/15 flex flex-wrap gap-1.5">
                        {post.suggested_tags.map((t) => (
                            <span key={t} className="text-[0.6rem] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full bg-gold/10 text-brown border border-gold/30">
                                {t}
                            </span>
                        ))}
                    </div>
                )}

                <div className="mt-14 rounded-sm border border-gold/30 bg-gold/5 p-6 md:p-8">
                    <p className="text-[0.65rem] tracking-[0.35em] uppercase text-gold font-semibold">
                        Join the Waitlist
                    </p>
                    <h3 className="font-serif text-2xl text-forest mt-2">
                        Be first in line when the first bar drops.
                    </h3>
                    <Link to="/inventory" className="btn-pill btn-primary mt-5 inline-flex">
                        See the First Drop
                    </Link>
                </div>
            </article>
        </div>
    );
}
